import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, finalize, share, tap, timeout } from 'rxjs/operators';

import { AdminApi } from '../../../admin/store/api/admin';
import { UserService } from '../../../core/services/user.service';
import { MessagesApi } from '../../../messages/store/api/messages';
import { useToaster } from '../../composables/use-toaster';
import { User } from '../../models/User';
import { setLoginUser } from '../actions/login';
import { envLoaded, startLoading } from '../actions/user-env';
import { AppState } from '../app-state';

@Injectable()
export class UserEnvApi {
    private readonly translate = inject(TranslateService);
    private readonly toaster = useToaster();

    constructor(
        private userService: UserService,
        private adminApi: AdminApi,
        private messageAPI: MessagesApi,
        private store: Store<AppState>,
    ) {}

    loadUserEnvironment(): Observable<unknown> {
        this.store.dispatch(startLoading());

        const getInstaller$: Observable<User> = this.userService.getInstaller().pipe(share());

        const userSetup$ = getInstaller$.pipe(
            tap((user: User) => {
                this.store.dispatch(setLoginUser({ loginuser: user }));
            }),
        );

        return userSetup$.pipe(
            tap(() => this.store.dispatch(envLoaded())),
            timeout(30 * 1000),
            catchError((err) => {
                console.error(err);
                this.toaster.error({
                    title: this.translate.instant('ERROR_SITES_TAKES_TO_LONG_TO_LOAD_OR_UNAVAILABLE'),
                    duration: 30 * 1000,
                });
                return of([]);
            }),
        );
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    loadAdminUserEnvironment(): Observable<any> {
        this.store.dispatch(startLoading());

        const currentBackOfficeUser$ = this.adminApi.getCurrentBackOfficeUserInfo().pipe(share());

        return forkJoin([this.messageAPI.getAdminMessages(), currentBackOfficeUser$]).pipe(
            // #SEG-794
            // When observable return empty response (like GET bo/users/associations), the success callback of subscribe()
            // or the piped actions like forkjoin, map, tap etc... are not called.
            // see link below
            // https://blog.novanet.no/rxjs-getting-fooled-by-empty-observables/
            finalize(() => this.store.dispatch(envLoaded())),
        );
    }
}
